import React, { ReactNode, useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/auth';
import { useUserProfile } from '@/contexts/profile/ProfileContext';
import { submitContactForm } from '@/utils/supabase/contactHelpers';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { ContactFormCategoryField } from '@/components/contact/ContactFormCategoryField';
import { ContactFormMessageField } from '@/components/contact/ContactFormMessageField';
import { ContactFormReportReasonField } from '@/components/contact/ContactFormReportReasonField';
import { ContactFormNameEmailPhone } from '@/components/contact/ContactFormNameEmailPhone';
import { ContactFormSchema, contactFormSchema } from '@/components/contact/schema';

type ContactFormValues = ContactFormSchema;

interface ContactFormDialogProps {
  trigger: ReactNode;
  initialCategory?: string;
  reviewDetails?: string;
  reviewId?: string;
  providerName?: string;
  providerId?: string;
}

const ContactFormDialog = ({
  trigger,
  initialCategory = "General Inquiry",
  reviewDetails,
  reviewId,
  providerName,
  providerId,
}: ContactFormDialogProps) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Unified user profile (cached)
  const { profile: userProfile } = useUserProfile();

  // Get user's full name from profile or user_metadata
  const getUserFullName = useCallback(() => {
    // First try to get name from profiles table
    if (userProfile?.first_name || userProfile?.last_name) {
      return `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim();
    }
    
    // Fallback to user_metadata
    const firstName = user?.user_metadata?.first_name || "";
    const lastName = user?.user_metadata?.last_name || "";
    return `${firstName} ${lastName}`.trim();
  }, [userProfile, user]);
  
  // If reviewDetails is provided, default the category to "Report a Review"
  const defaultCategory = reviewDetails ? "Report a Review" : initialCategory;
  const isReportingReview = defaultCategory === "Report a Review" && !!reviewId;
  const shouldDisableUserFields = !!user;
  
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      category: defaultCategory,
      message: reviewDetails || "",
      report_reason: "",
      phone_number: "",
    },
  });

  // Update form values when user profile data loads
  useEffect(() => {
    if (user) {
      form.setValue('name', getUserFullName());
      form.setValue('email', user.email || "");
      form.setValue('phone_number', userProfile?.whatsapp_number || user?.phone || "");
    }
  }, [user, userProfile, form, getUserFullName]);

  const onSubmit = async (values: ContactFormValues) => {
    setIsSubmitting(true);
    try {
      const currentUrl = window.location.href;
      
      const success = await submitContactForm({
        name: values.name,
        email: values.email,
        category: values.category,
        message: values.message,
        user_id: user?.id,
        review_id: reviewId,
        report_reason: values.report_reason,
        page_url: currentUrl,
        provider_id: providerId,
        provider_name: providerName,
        phone_number: values.phone_number
      });

      if (success) {
        toast.success("Your message has been sent successfully!");
        setOpen(false);
        form.reset();
      } else {
        toast.error("There was a problem sending your message. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again later.");
      console.error("Contact form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Contact Us</DialogTitle>
          <DialogDescription>
            Send us a message and we'll get back to you as soon as possible.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <ContactFormNameEmailPhone form={form} disabledUserFields={shouldDisableUserFields} />
            <ContactFormCategoryField form={form} userPresent={!!user} isReportingReview={isReportingReview} />
            <ContactFormReportReasonField form={form} activeCategory={form.watch('category')} />
            <ContactFormMessageField form={form} />

            {reviewDetails && (
              <div className="text-sm bg-amber-50 border border-amber-200 p-3 rounded-md flex items-start">
                <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-amber-800">
                  You are reporting a review. Our team will review your report
                  and take appropriate action.
                </p>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Message"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactFormDialog;
